import { useEffect, useMemo, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  updateOperationSources,
  handleBunkeringAndPdf,
  getLocalStartEnd,
  deriveLatestTagsFromPdfSetting,
} from "../utils/OperationPDFs/pdfs.utils";
import { selectSensorData } from "../services/selector/select.sensor.data";
import bunkeringTags from "../Components/AddElementPanel2/Components/WidgetSelectors/CanvasPanel/utils/bunkeringTags";

const useOperationPdf = () => {
  const dispatch = useDispatch();
  const pdfSettings = useSelector((s) => s.operationPdf.pdfSettings);
  const wsRequestData = useSelector((s) => s.appConfig.wsRequestData);
  const liveData = useSelector((s) => s.liveData);
  const isPlaybackMode = useSelector((s) => s.appConfig?.isPlaybackMode);

  const prevValsRef = useRef(new Map());
  const lastTimeRef = useRef(new Map());
  const liveDataRef = useRef(liveData);

  useEffect(() => {
    liveDataRef.current = liveData;
  }, [liveData]);

  const cfg = useMemo(
    () =>
      (pdfSettings || []).map((p) => ({
        id: p.id,
        name: p.name,
        triggerTag: p?.triggerTag || null,
        resetTag: p?.resetTag || null,
        startTag: p?.startTsTag || null,
        endTag: p?.endTsTag || null,
        title: p?.name || "",
        prologue: p?.prologue || "",
        epilogue: p?.epilogue || "",
        emails: Array.isArray(p?.emails)
          ? p.emails.filter((e) => typeof e === "string" && e.trim())
          : [p?.email],
        plc: p?.plc?.value || null,
        tsState: p?.triggerTimestampState,
        tsValue: p?.triggerTimestamp,
        rows: p?.rows || [],
        barChart: p?.barChart || [],
      })),
    [pdfSettings]
  );

  /* SYNC SOURCES */
  useEffect(() => {
    updateOperationSources(dispatch, pdfSettings, wsRequestData);
  }, [dispatch, pdfSettings, wsRequestData]);

  /* RISING-EDGE TRIGGER */
  useEffect(() => {
    if (!cfg.length) return;

    cfg.forEach((p) => {
      if (!p.plc) return;

      const t = p.triggerTag;
      if (!t?.ship || !t?.value) return;
      if (isPlaybackMode) return;

      const curr = selectSensorData({ liveData }, t.ship, t.value);
      const prev = prevValsRef.current.get(p.id);
      if ((prev === 0 && curr === 1) || (prev === false && curr === true)) {
        // if ((prev === 0 && curr === 1) || (prev === false && curr === true)) {
        const s = p.startTag,
          e = p.endTag;
        const startVal =
          s?.ship && s?.value
            ? selectSensorData({ liveData }, s.ship, s.value)
            : null;
        const endVal =
          e?.ship && e?.value
            ? selectSensorData({ liveData }, e.ship, e.value)
            : null;

        const derivedLatestTags = deriveLatestTagsFromPdfSetting(p);

        console.log("Derived Latest Tags:", derivedLatestTags);
        /* FIRE REQUEST ON RISING EDGE */
        console.log("Operation PDF Triggered:", {
          ship: t.ship,
          triggerTagValue: t.value,
          start: startVal,
          end: endVal,
          latestTags: derivedLatestTags,
        });
        handleBunkeringAndPdf({
          ship: t.ship,
          start: startVal,
          end: endVal,
          latestTags: derivedLatestTags,
          triggerTagValue: t.value,
          resetTagValue: p.resetTag?.value,
          title: p.title,
          prologue: p.prologue,
          epilogue: p.epilogue,
          emails: p.emails,
          rows: p.rows,
          plc: p.plc,
          barChart: p?.barChart,
        });
      }

      prevValsRef.current.set(p.id, curr);
    });
  }, [liveData, cfg, isPlaybackMode]);

  /* TIME-BASED TRIGGERS */
  useEffect(() => {
    const hasEnabled = cfg.some((p) => p.tsState === "enabled" && p.tsValue);

    if (!hasEnabled) return;

    const tick = () => {
      const hhmm = new Date().toISOString().slice(11, 16);
      const now = new Date();
      const dayKey = now.toISOString().slice(0, 10);

      cfg.forEach((p) => {
        if (!p.plc) return;

        /* RECEIVE START HOUR (END = START-2MINS) */
        const { start, end } = getLocalStartEnd(p.tsValue);

        if (p.tsState !== "enabled" || !p.tsValue) return;
        if (p.tsValue !== hhmm) return;
        const key = `${dayKey}-${hhmm}`;

        const last = lastTimeRef.current.get(p.id);
        if (last === key) return;

        const t = p.triggerTag;
        const derivedLatestTags = deriveLatestTagsFromPdfSetting(p).length
          ? deriveLatestTagsFromPdfSetting(p)
          : (bunkeringTags?.latestTags || []).filter(Boolean);

        handleBunkeringAndPdf({
          ship: t?.ship,
          start: start,
          end: end,
          latestTags: derivedLatestTags,
          triggerTagValue: t?.value,
          title: p.title,
          prologue: p.prologue,
          epilogue: p.epilogue,
          emails: p.emails,
          rows: p.rows,
          plc: p.plc,
          barChart: p?.barChart,
        });

        lastTimeRef.current.set(p.id, key);
      });
    };

    tick();
    const id = setInterval(tick, 10000);
    return () => clearInterval(id);
  }, [cfg]);

  /* MAP ROWS -> INJECT VALUES is now in utils (mapRowsToLatestRecord) */
};

export default useOperationPdf;
